import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../commponents/layout/adminLayout";
import DocumentInformation, {
  type DocumentFieldConfig,
} from "../../commponents/document/DocumentInformation";

import StatusDropdown, { type status } from "../../commponents/StatusDropdown";
import { editInvoice, getInvoiceById, getInvoiceStatus } from "../../api/invoiceApi";
import { getSales } from "../../api/salesApi";

import type { ItemRow } from "../../commponents/document/DocumentItemTable";
import DocumentItemTable from "../../commponents/document/DocumentItemTable";
import DocumentSummary from "../../commponents/document/DocumentSummary";


import {
  type FieldErrors,
  type ItemsErrors,
  runFieldValidation,
  requiredText,
  positiveNumber,
  validateItems,
  validateNumberRange,
  withErrorClear,
  focusFirstError,
} from "../../helpers/validation";
import { showError, showSuccess } from "../../utils/ alert";
import type { CustomerOption } from "../../api/customerApi";
import CustomerPicker from "../../commponents/document/CustomerPicker";

interface SalesOption {
  id: number;
  name: string;
}

interface InvoiceFormValue {
  recipient_name: string;
  address: string;
  phone: string;
  invoice_no?: string;
  invoice_date: string;
  due_date: string;
  sales_id: number | "";
  po_number: string;

  totalPaymentDue: string;
  delivery_time: string;
}

export default function EditInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [invoiceNo, setInvoiceNo] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceDueDate, setInvoiceDueDate] = useState("");

  const [salesId, setSalesId] = useState<number | "">("");
  const [salesOptions, setSalesOptions] = useState<SalesOption[]>([]);
  const [invoiceStatusId, setInvoiceStatusId] = useState<number>(0);
  const [statusOptions, setStatusOptions] = useState<status[]>([]);
  const [poNumber, setPoNumber] = useState("");
  const [paymentDue, setPaymentDue] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");

  // Optional link to an existing customer record.
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerIdRef, setCustomerIdRef] = useState<number | null>(null);

  const [items, setItems] = useState<ItemRow[]>([]);
  const [discount, setDiscount] = useState(0);
  const [ppn, setPpn] = useState(11);

  // --- validation state -------------------------------------------------
  const [errors, setErrors] = useState<FieldErrors>({});
  const [itemsErrors, setItemsErrors] = useState<ItemsErrors>({});
  const [itemsGeneralError, setItemsGeneralError] = useState<string | undefined>();
  const [summaryErrors, setSummaryErrors] = useState<FieldErrors>({});

  const subtotal = items.reduce((sum, item) => sum + item.qty * item.price, 0);
  const discountAmount = subtotal * (discount / 100);
  const totalAfterDiscount = subtotal - discountAmount;
  const ppnAmount = totalAfterDiscount * (ppn / 100);
  const grandTotal = totalAfterDiscount + ppnAmount;

  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await getSales({
          page: 1,
          limit: 10,
        });
        setSalesOptions(response.data.salesList ?? []);
      } catch (error) {
        showError(
        "Failed",
        "Failed to Load data."
    );
      }
    };

    fetchSales();
  }, []);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);

        const response = await getInvoiceById(Number(id));
        const data = response.data.data ?? response.data;

        setInvoiceNo(data.invoice_no);
        setRecipientName(data.customer.company_name ?? "");
        setAddress(data.customer.address ?? "");
        setPhone(data.customer.phone ?? "");
        setInvoiceDate(
          data.invoice_date ? String(data.invoice_date).slice(0, 10) : ""
        );

        setInvoiceDueDate(
          data.due_date ? String(data.due_date).slice(0, 10) : ""
        );

        // Handle baik data.sales_id (kolom langsung) maupun data.sales.id
        // (kalau backend include association "sales")
        setSalesId(data.sales_id ?? data.sales?.id ?? null);
        setInvoiceStatusId(
          data.invoice_status_id ?? data.invoiceStatus?.id ?? null
        );

        setPoNumber(data.po_number ?? "");
        setPaymentDue(data.payment_due ?? "");
        setDeliveryTime(data.delivery_time ?? "");

        setItems(
          (data.items ?? []).map((item: any) => ({
            description: item.description,
            qty: Number(item.qty),
            unit: item.unit,
            price: Number(item.price),
            product_id: item.product_id ?? null,
          }))
        );

        setDiscount(Number(data.discount ?? 0));
        setPpn(Number(data.ppn ?? 11));

        if (data.customer) {
          setCustomerIdRef(data.customer.id);
          setCustomerQuery(
            data.customer.company_name
              ? `${data.customer.customer_name} (${data.customer.company_name})`
              : data.customer.customer_name
          );
        }
      } catch (error) {
        showError(
        "Failed",
        "Failed to Load data."
    );
        showError("Failed to load invoice.");
        
        navigate("/invoice");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchInvoice();
  }, [id]);

  const handleSelectCustomer = (customer: CustomerOption) => {
    setCustomerIdRef(customer.id);
    setCustomerQuery(
      customer.companyName
        ? `${customer.customerName} (${customer.companyName})`
        : customer.customerName
    );
    setRecipientName(customer.companyName ?? customer.customerName);
    setAddress(customer.address ?? "");
    setPhone(customer.phone ?? "");
  
  };

  const handleClearCustomer = () => {
    setCustomerIdRef(null);
    setCustomerQuery("");
  };

  const handleCancel = () => navigate("/invoice");

  const validate = () => {
    const fieldErrors = runFieldValidation<InvoiceFormValue>(
      {
        recipient_name: recipientName,
        address,
        phone,
        invoice_date: invoiceDate,
        due_date: invoiceDueDate,
        sales_id: salesId,
        po_number: poNumber,

        totalPaymentDue: paymentDue,
        delivery_time: deliveryTime,
      },
      {
        totalPaymentDue: requiredText("Payment Due is required"),
      
        invoice_date: requiredText("Invoice date is required"),
      },
    );

    const { itemsErrors: rowErrors, hasError: itemsHasError, generalError } = validateItems(
      items,
      {
        description: requiredText("Description is required"),
        qty: positiveNumber("Quantity must be greater than 0"),
        unit: requiredText("Unit is required"),
        price: positiveNumber("Price must be greater than 0"),
      }
    );

    const nextSummaryErrors: FieldErrors = {};
    const discountError = validateNumberRange(discount, 0, 100, "Discount must be between 0 and 100");
    const ppnError = validateNumberRange(ppn, 0, 100, "PPN must be between 0 and 100");
    if (discountError) nextSummaryErrors.discount = discountError;
    if (ppnError) nextSummaryErrors.ppn = ppnError;

    setErrors(fieldErrors);
    setItemsErrors(rowErrors);
    setItemsGeneralError(generalError);
    setSummaryErrors(nextSummaryErrors);

    const hasAnyError =
      Object.keys(fieldErrors).length > 0 ||
      itemsHasError ||
      Object.keys(nextSummaryErrors).length > 0;

    if (hasAnyError) {
      focusFirstError({
        fieldErrors,
        itemsHasError,
        itemsSectionId: "invoice-items-section",
        summaryHasError: Object.keys(nextSummaryErrors).length > 0,
        summarySectionId: "invoice-summary-section",
      });
    }

    return !hasAnyError;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);

      await editInvoice(Number(id), {
      
        sales_id: Number(salesId),
        po_number: poNumber,
       totalPaymentDue: paymentDue,
        delivery_time: deliveryTime,
        place_of_delivery: address,
        invoice_date: invoiceDate,
        due_date:invoiceDueDate,
        discount,
        ppn,
        invoice_status_id: invoiceStatusId,
        customer_id_ref: customerIdRef,
        items,
      });

      await showSuccess("Success", "Invoice updated successfully.");
      navigate("/invoice");
    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
      showError("Failed to update invoice.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await getInvoiceStatus();

        const statusList = response.data.invoiceList ?? [];

        setStatusOptions(statusList);
      } catch (error) {
        showError(
        "Failed",
        "Failed to Load data."
    );
      }
    };

    loadStatus();
  }, []);

  const invoiceFields: DocumentFieldConfig<InvoiceFormValue>[] = [
    { key: "recipient_name", label: "Bill To", type: "readonly", column: 1 },
    { key: "address", label: "Address", type: "readonly", column: 1 },
    { key: "phone", label: "Phone", type: "readonly", column: 1 },
    { key: "invoice_no", label: "No Invoice", type: "readonly", column: 1 },
    { key: "invoice_date", label: "Date", type: "date", column: 1 },
    {
      key: "sales_id",
      label: "Sales",
      type: "select",
      column: 2,
      numeric: true,
      placeholderOption: { value: "", label: "Select Sales" },
      options: salesOptions.map((sales) => ({
        value: sales.id,
        label: sales.name,
      })),
    },
    { key: "po_number", label: "PO Number", type: "text", column: 2 },

    { key: "due_date", label: "Due Date", type: "date", column: 2 },
    {
      key: "totalPaymentDue",
      label: "Total Payment Due",
      type: "text",
      column: 2,
      placeholder: "e.g. 30 days",
    },
    { key: "delivery_time", label: "Delivery Time", type: "text", column: 2 },
  ];

  const handleFieldChange = <K extends keyof InvoiceFormValue>(
    key: K,
    value: InvoiceFormValue[K]
  ) => {
    switch (key) {
      
      case "invoice_date":
        setInvoiceDate(value as string);
        break;

      case "due_date":
        setInvoiceDueDate(value as string);
        break;
      case "sales_id":
        setSalesId(value as number | "");
        break;
      case "po_number":
        setPoNumber(value as string);
        break;
      case "totalPaymentDue":
        setPaymentDue(value as string);
        break;

      case "delivery_time":
        setDeliveryTime(value as string);
        break;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center text-gray-400">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Invoice</h2>
          <div>
            <label className="mr-3 text-sm text-gray-600">Status</label>
            <StatusDropdown
              options={statusOptions}
              value={invoiceStatusId}
              onChange={setInvoiceStatusId}
            />
          </div>
        </div>

        <CustomerPicker
          query={customerQuery}
          onQueryChange={setCustomerQuery}
          onSelectCustomer={handleSelectCustomer}
          selectedCustomerId={customerIdRef}
          onClearSelection={handleClearCustomer}
        />

        <DocumentInformation
          value={{
            recipient_name: recipientName,
            address,
            phone,
            invoice_no: invoiceNo,
            invoice_date: invoiceDate,
            sales_id: salesId,
            po_number: poNumber,
            due_date: invoiceDueDate,
            totalPaymentDue: paymentDue,
            delivery_time: deliveryTime,
          }}
          fields={invoiceFields}
          labelWidthClass="w-32"
          errors={errors}
          onChange={withErrorClear(handleFieldChange, setErrors)}
        />

        <div className="my-6 border-t border-gray-100" />

        <div id="invoice-items-section">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">
            Invoice Item
          </h3>
          <DocumentItemTable items={items} onChange={setItems} />
          {itemsGeneralError && (
            <p className="mt-2 text-xs text-red-500">{itemsGeneralError}</p>
          )}
          {Object.keys(itemsErrors).length > 0 && (
            <div className="mt-2 space-y-1">
              {Object.entries(itemsErrors).map(([rowIndex, rowErrors]) => (
                <p key={rowIndex} className="text-xs text-red-500">
                  Row {Number(rowIndex) + 1}:{" "}
                  {Object.values(rowErrors).join(", ")}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="my-6 border-t border-gray-100" />

        <div id="invoice-summary-section">
          <DocumentSummary
            items={items}
            discount={discount}
            ppn={ppn}
            onDiscountChange={setDiscount}
            onPpnChange={setPpn}
          />
          {summaryErrors.discount && (
            <p className="mt-1 text-xs text-red-500">
              {summaryErrors.discount}
            </p>
          )}
          {summaryErrors.ppn && (
            <p className="mt-1 text-xs text-red-500">{summaryErrors.ppn}</p>
          )}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="rounded-xl border border-red-200 px-5 py-2.5 font-medium text-red-500 hover:bg-red-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting}
            className="rounded-xl bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
